import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';

import {
  backupSettingsClient,
  CustomerBackups,
  JobBackupSetting,
  OrderBackups,
} from './api';

import { apiErrorMessage } from '@/routes/Agency/DashboardV2/util/apiError';

// One root for every backup-settings read, so a write at any level refreshes
// all of them: turning a client off changes what each of its orders and jobs
// shows, and an order write changes every job row under it.
const ROOT = 'backup_settings' as const;

export const backupSettingsKeys = {
  root: [ROOT] as const,
  customer: (customerId: number | string) =>
    [ROOT, 'customer', String(customerId)] as const,
  order: (orderId: number | string) =>
    [ROOT, 'order', String(orderId)] as const,
  job: (jobId: number | string) => [ROOT, 'job', String(jobId)] as const,
};

export const useCustomerBackups = (
  customerId: number | string,
  enabled = true,
) =>
  useQuery<CustomerBackups>({
    queryKey: backupSettingsKeys.customer(customerId),
    queryFn: () => backupSettingsClient.getCustomer(customerId),
    enabled,
    retry: false,
  });

/** One read feeds the order's control and every job row under it. */
export const useOrderBackups = (
  orderId: number | string | undefined,
  enabled = true,
) =>
  useQuery<OrderBackups>({
    queryKey: backupSettingsKeys.order(orderId ?? ''),
    queryFn: () => backupSettingsClient.getOrder(orderId as string),
    enabled: enabled && orderId != null,
    retry: false,
  });

export const useJobBackupSetting = (
  jobId: number | string | undefined,
  enabled = true,
) =>
  useQuery<JobBackupSetting>({
    queryKey: backupSettingsKeys.job(jobId ?? ''),
    queryFn: () => backupSettingsClient.getJob(jobId as string),
    enabled: enabled && jobId != null,
    retry: false,
  });

/**
 * A save at one level. Returns false on failure and leaves the API's own
 * message (`errors[0]`) in `error` for the control to show inline.
 *
 * Every outcome refreshes all backup settings AND Dashboard V2: the calendar
 * cards carry "Backups off" and the Invite backups modal reads the job's
 * setting, and a 422 usually means the state moved under the screen.
 */
export const useBackupSave = <T>(
  write: (enabled: boolean | null) => Promise<T>,
  fallback: string,
  onSaved?: (result: T, enabled: boolean | null) => void,
) => {
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Latest callbacks without re-creating `save` on every render.
  const latest = useRef({ write, onSaved });
  latest.current = { write, onSaved };

  const save = useCallback(
    async (enabled: boolean | null): Promise<boolean> => {
      setSaving(true);
      setError(null);
      try {
        const result = await latest.current.write(enabled);
        latest.current.onSaved?.(result, enabled);
        return true;
      } catch (e) {
        setError(apiErrorMessage(e) ?? fallback);
        return false;
      } finally {
        setSaving(false);
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: backupSettingsKeys.root }),
          queryClient.invalidateQueries({ queryKey: ['dashboard_v2'] }),
        ]);
      }
    },
    [fallback, queryClient],
  );

  return { save, saving, error };
};

/** A change the admin asked for that is waiting on the warning's Confirm. */
export type BackupChoice = {
  /** The exact body value to send: true, false, or null to inherit. */
  enabled: boolean | null;
  /** What the level will be once saved — decides which warning is shown. */
  turningOn: boolean;
};

/**
 * Holds a change until its warning is confirmed. Turning backups on or off at
 * any level always asks first; clearing an override that leaves the value
 * where it is changes nothing that runs, so it is saved straight away.
 */
export const useBackupChoice = (
  save: (enabled: boolean | null) => Promise<boolean>,
) => {
  const [choice, setChoice] = useState<BackupChoice | null>(null);

  const request = useCallback(
    (enabled: boolean | null, resulting: boolean, current: boolean) => {
      if (resulting === current) {
        void save(enabled);
        return;
      }
      setChoice({ enabled, turningOn: resulting });
    },
    [save],
  );

  const cancel = useCallback(() => setChoice(null), []);

  const confirm = useCallback(async () => {
    if (!choice) return;
    setChoice(null);
    await save(choice.enabled);
  }, [choice, save]);

  return { choice, request, cancel, confirm };
};

export type BackupChooser = ReturnType<typeof useBackupChoice>;
